import { useEffect, useState } from 'react';
import client from '../api/client';

export default function CancellationRequests() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('PENDING');
  const [selected, setSelected] = useState(null);
  const [modal, setModal] = useState(null); // 'approve' | 'decline'
  const [note, setNote] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const inp = { padding:'10px 14px', background:'#f9f9f9', border:'1px solid #ddd', borderRadius:8, color:'#000', fontSize:14, outline:'none' };

  const load = () => {
    setLoading(true);
    client.get('/admin/cancellation-requests?status=' + statusFilter)
      .then(r => setRequests(r.data.requests || []))
      .catch(() => setRequests([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [statusFilter]);

  const openModal = (req, type) => {
    setSelected(req);
    setNote('');
    setErr('');
    setModal(type);
  };

  const doAction = async () => {
    setErr('');
    setActionLoading(true);
    try {
      if (modal === 'approve') {
        const r = await client.post('/admin/cancellation-requests/' + selected.id + '/approve', { note });
        setMsg('Cancellation approved. Subscription will end on ' + (r.data.accessEndDate || 'period end') + '. Tenant notified.');
      } else if (modal === 'decline') {
        await client.post('/admin/cancellation-requests/' + selected.id + '/decline', { note });
        setMsg('Request declined. Tenant has been notified.');
      }
      setModal(null);
      load();
    } catch (e) {
      setErr(e.response?.data?.error || e.response?.data?.message || e.response?.data || 'Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (s) => {
    if (!s) return '';
    try { return new Date(s).toLocaleString(); } catch (e) { return s; }
  };

  const statusBadge = (s) => {
    const colors = {
      PENDING:  { bg: '#fff8ec', fg: '#b45309' },
      APPROVED: { bg: '#f0fff6', fg: '#0a8a40' },
      DECLINED: { bg: '#f5f5f5', fg: '#555' },
    };
    const c = colors[s] || colors.DECLINED;
    return <span style={{ background:c.bg, color:c.fg, padding:'3px 8px', borderRadius:4, fontSize:12, fontWeight:600 }}>{s}</span>;
  };

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:24 }}>
        <h1 style={{ color:'#000', fontSize:22, fontWeight:700, margin:0, fontFamily:'Georgia,serif' }}>Cancellation Requests</h1>
        <div style={{ color:'#555', fontSize:14 }}>{requests.length} request{requests.length === 1 ? '' : 's'}</div>
      </div>

      <div style={{ display:'flex', gap:12, marginBottom:20 }}>
        <select style={inp} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="PENDING">Pending review</option>
          <option value="APPROVED">Approved</option>
          <option value="DECLINED">Declined</option>
        </select>
      </div>

      {msg && (
        <div style={{ background:'#f0fff6', border:'1px solid #b2dfdb', borderRadius:8, padding:'10px 16px', color:'#0a8a40', fontSize:13, marginBottom:16 }}>
          {msg} <button onClick={() => setMsg('')} style={{ background:'none', border:'none', color:'#0a8a40', cursor:'pointer', float:'right' }}>✕</button>
        </div>
      )}

      <div style={{ background:'#fff', borderRadius:12, border:'1px solid #eee', overflow:'hidden' }}>
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead>
            <tr style={{ borderBottom:'1px solid #eee' }}>
              {['Tenant','Plan','Reason','Submitted','Status','Reviewed By','Actions'].map(h => (
                <th key={h} style={{ color:'#555', fontSize:11, fontWeight:500, textAlign:'left', padding:'14px 16px', textTransform:'uppercase' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} style={{ color:'#555', padding:24, textAlign:'center' }}>Loading...</td></tr>
            ) : requests.length === 0 ? (
              <tr><td colSpan={7} style={{ color:'#888', padding:32, textAlign:'center' }}>No {statusFilter.toLowerCase()} requests</td></tr>
            ) : requests.map(r => (
              <tr key={r.id} style={{ borderBottom:'1px solid #f5f5f5' }}>
                <td style={{ padding:'12px 16px' }}>
                  <div style={{ color:'#000', fontSize:14, fontWeight:500 }}>{r.tenant_name}</div>
                  <div style={{ color:'#555', fontSize:12 }}>{r.tenant_email}</div>
                  {r.requested_by_email && r.requested_by_email !== r.tenant_email && (
                    <div style={{ color:'#888', fontSize:11, marginTop:2 }}>Requested by: {r.requested_by_email}</div>
                  )}
                </td>
                <td style={{ padding:'12px 16px', color:'#333', fontSize:13 }}>{r.plan}</td>
                <td style={{ padding:'12px 16px', color:'#444', fontSize:13, maxWidth:260 }}>
                  {r.reason ? r.reason : <span style={{ color:'#bbb', fontStyle:'italic' }}>No reason given</span>}
                </td>
                <td style={{ padding:'12px 16px', color:'#555', fontSize:12 }}>{formatDate(r.created_at)}</td>
                <td style={{ padding:'12px 16px' }}>{statusBadge(r.status)}</td>
                <td style={{ padding:'12px 16px', color:'#555', fontSize:12 }}>
                  {r.reviewed_by_admin_name ? (
                    <>
                      <div>{r.reviewed_by_admin_name}</div>
                      <div style={{ fontSize:11, color:'#888' }}>{formatDate(r.reviewed_at)}</div>
                      {r.admin_note && <div style={{ fontSize:11, color:'#888', fontStyle:'italic', marginTop:2 }}>"{r.admin_note}"</div>}
                    </>
                  ) : '—'}
                </td>
                <td style={{ padding:'12px 16px' }}>
                  {r.status === 'PENDING' ? (
                    <div style={{ display:'flex', gap:6 }}>
                      <button onClick={() => openModal(r, 'approve')}
                        style={{ padding:'7px 14px', background:'#0a8a40', color:'#fff', border:'none', borderRadius:6, cursor:'pointer', fontSize:13, fontWeight:600 }}>
                        Approve
                      </button>
                      <button onClick={() => openModal(r, 'decline')}
                        style={{ padding:'7px 14px', background:'#fff', color:'#c0392b', border:'1px solid #c0392b', borderRadius:6, cursor:'pointer', fontSize:13, fontWeight:600 }}>
                        Decline
                      </button>
                    </div>
                  ) : <span style={{ color:'#bbb', fontSize:12 }}>Closed</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Action Modal */}
      {modal && selected && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}>
          <div style={{ background:'#fff', borderRadius:12, padding:32, width:460, border:'1px solid #eee' }}>
            <h3 style={{ color: modal === 'approve' ? '#0a8a40' : '#c0392b', margin:'0 0 6px', fontFamily:'Georgia,serif' }}>
              {modal === 'approve' ? 'Approve Cancellation' : 'Decline Request'}
            </h3>
            <div style={{ color:'#555', fontSize:13, marginBottom:18 }}>
              {selected.tenant_name} ({selected.tenant_email}) — {selected.plan} plan
            </div>

            {modal === 'approve' ? (
              <div style={{ background:'#fff8ec', border:'1px solid #fcd34d', borderRadius:8, padding:14, marginBottom:16, color:'#92400e', fontSize:13, lineHeight:1.6 }}>
                <strong>This will:</strong>
                <ul style={{ margin:'8px 0 0', paddingLeft:20 }}>
                  <li>Cancel the Stripe subscription at end of current billing period</li>
                  <li>Email the tenant with confirmation + access end date</li>
                  <li>Notify your admin team</li>
                  <li>Move the tenant account to read-only on the cancellation date</li>
                </ul>
              </div>
            ) : (
              <div style={{ background:'#f5f5f5', border:'1px solid #ddd', borderRadius:8, padding:14, marginBottom:16, color:'#444', fontSize:13, lineHeight:1.6 }}>
                The tenant will receive an email letting them know we're not proceeding with cancellation. Their subscription stays active.
              </div>
            )}

            <label style={{ color:'#333', fontSize:12, fontWeight:700, display:'block', marginBottom:6, textTransform:'uppercase' }}>
              {modal === 'approve' ? 'Internal note (optional)' : 'Reason for declining (visible to tenant)'}
            </label>
            <textarea value={note} onChange={e => setNote(e.target.value)} rows={3}
              placeholder={modal === 'approve' ? 'e.g. Confirmed via call with John on 5/10' : 'e.g. Offered 20% discount, customer agreed to stay'}
              style={{ width:'100%', padding:'10px 12px', border:'1px solid #ddd', borderRadius:8, fontSize:13, fontFamily:'inherit', resize:'vertical', boxSizing:'border-box', outline:'none', marginBottom:14 }} />

            {err && <div style={{ background:'#fff5f5', border:'1px solid #fbb', borderRadius:8, padding:'8px 12px', color:'#c0392b', fontSize:12, marginBottom:12 }}>{err}</div>}

            <div style={{ display:'flex', gap:10 }}>
              <button onClick={doAction} disabled={actionLoading}
                style={{ flex:1, padding:'11px', background: modal === 'approve' ? '#0a8a40' : '#c0392b', color:'#fff', border:'none', borderRadius:8, fontSize:14, fontWeight:700, cursor: actionLoading ? 'not-allowed' : 'pointer', opacity: actionLoading ? 0.6 : 1 }}>
                {actionLoading ? 'Working...' : (modal === 'approve' ? 'Approve & Cancel Subscription' : 'Decline Request')}
              </button>
              <button onClick={() => setModal(null)}
                style={{ flex:1, padding:'11px', background:'#fff', color:'#555', border:'1px solid #ddd', borderRadius:8, fontSize:14, fontWeight:700, cursor:'pointer' }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
